import { useContext, useEffect, useState } from "react";
import * as Yup from "yup";
import { useManualUpload, useUserLogin } from "./index";
import { AuthContext } from "../components/AuthContextComponent";
import { QuestionList, updatePassword } from "../api/FetchAPI";
import {
  generateLoading,
  showAlert,
  stopLoading,
} from "../promptMessages/PromptMessages";
import { SecurityQuestion } from "../lib/interfaces/index";
import { validationSchema } from "../lib/helpers/index";
import useHeadComponent from "./HeaderHook";
import { FetchErr } from "../lib/helpers/UnhandledError";
import { handleHttpError } from "../lib/helpers/HanldeHttpError";

// Ensure Yup is imported correctly

const useProfile = () => {
  const { selectedPartner } = useManualUpload();
  const { Balance } = useHeadComponent(selectedPartner);
  const { customColor } = useUserLogin();
  const userInfo = useContext(AuthContext);
  const { setCredentials } = useContext(AuthContext);
  const { username } = useContext(AuthContext);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isAbout, setIsAbout] = useState(false);
  const [isPassword, setIsPassword] = useState(false);
  const [isTheme, setIsTheme] = useState(false);
  const [isSecretQ, setSecretQuestion] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isNewError, setIsNewError] = useState(false);
  const [isNewPasswordError, setIsNewPasswordError] = useState(false);
  const [isConfirmPasswordError, setIsConfirmPasswordError] = useState(false);
  const [questions, setQuestions] = useState<SecurityQuestion[]>([]);
  const [formValues, setFormValues] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const themeBackground = userInfo?.userInfo?.theme === "DARK" ? "#24292e" : "";
  const themeTextColor = userInfo?.userInfo?.theme === "DARK" ? "#fafbfc" : "";
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const fullName = [
    userInfo.userInfo?.userFName,
    userInfo.userInfo?.userMName,
    userInfo.userInfo?.userLName,
  ]
    .filter(Boolean)
    .join(" ");

  const email = userInfo.userInfo?.userEmail || "";
  const contactNo = userInfo.userInfo?.userContactNo;
  const fName = userInfo.userInfo?.userFName;
  const lName = userInfo.userInfo?.userLName;
  const mName = userInfo.userInfo?.userMName;
  const partnersName = userInfo.userInfo?.partnersName;
  const category = userInfo.userInfo?.category;

  //hanlde to get question list
  const questionList = async () => {
    try {
      const response: any = await QuestionList(username);
      setQuestions(response);
    } catch (error: any) {
      // FetchErr(error, themeBackground, customColor, themeTextColor);
      handleHttpError(error, customColor);
    }
  };

  useEffect(() => {
    questionList();
  }, [username]);

  const handleAboutClick = () => {
    setIsPassword(false);
    setIsTheme(false);
    setSecretQuestion(false);
    setIsAbout(true);
  };

  const handlePasswordClick = () => {
    setIsAbout(false);
    setIsTheme(false);
    setSecretQuestion(false);
    setIsPassword(true);
  };

  const handleThemeClick = () => {
    setIsAbout(false);
    setIsPassword(false);
    setSecretQuestion(false);
    setIsTheme(true);
  };

  const handleSecretClick = () => {
    setIsAbout(false);
    setIsPassword(false);
    setIsTheme(false);
    setSecretQuestion(true);
  };

  const handleOldPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOldPassword(e.target.value);
    if (isNewError) setIsNewError(false);
  };

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setNewPassword(newPassword);
    setIsError(false);
    setIsNewError(false);
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const confirmPassword = e.target.value;
    setConfirmPassword(confirmPassword);
    setIsError(false);
    setIsNewError(false);

    // Check if it matches the new password
    if (confirmPassword !== newPassword) {
      setIsConfirmPasswordError(true); // Indicate that the confirmation does not match
    } else {
      setIsConfirmPasswordError(false); // Reset error if it matches
    }
  };
  //hanle reset form values
  const resetForm = () => {
    setConfirmPassword("");
    setOldPassword("");
    setNewPassword("");
  };
  // handle change for form values
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });

    setErrors((prevErrors) => {
      const { [name]: _, ...rest } = prevErrors;
      return rest;
    });
  };
  //hanlde for submit password change
  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
    handleShowSecurityQuestion?: () => void
  ) => {
    e.preventDefault();

    try {
      await validationSchema.validate(formValues, { abortEarly: false });
      generateLoading("Please wait...", themeBackground, themeTextColor);
      const response: any = await updatePassword(
        username,
        formValues.currentPassword,
        formValues.confirmNewPassword
      );
      if (response.respCode === 2000) {
        setTimeout(() => {
          if (userInfo.userInfo) {
            const updatedUserInfo = {
              ...userInfo.userInfo,
              pwdUpdate: false,
            };
            setCredentials(updatedUserInfo);
          }
          stopLoading();
          showAlert(
            "Success",
            "Password changed successfully",
            "success",
            customColor,
            themeBackground,
            themeTextColor,
            handleShowSecurityQuestion
          );
          resetForm();
          setIsPassword(false);
          setIsError(false);
          setIsAbout(true);
        }, 1000);
        setFormValues({
          currentPassword: "",
          newPassword: "",
          confirmNewPassword: "",
        });
      } else {
        // generateLoading("Please wait...");
        stopLoading();
        setTimeout(() => {
          showAlert(
            "Something went wrong!",
            response.respMsg,
            "error",
            customColor,
            themeBackground,
            themeTextColor
          );
        }, 1000);
      }

      setErrors({});
    } catch (error) {
      const validationErrors: { [key: string]: string } = {};
      if (validationErrors) {
        if (error instanceof Yup.ValidationError) {
          error.inner.forEach((error) => {
            if (error.path) {
              validationErrors[error.path] = error.message;
            }
          });
        }
        setErrors(validationErrors);
      } else {
        handleHttpError(error, customColor);
      }
    }
  };
  //handle clear form values
  const handleClear = () => {
    setFormValues({
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    });
    setErrors({});
  };

  return {
    Balance,
    category,
    contactNo,
    email,
    fName,
    mName,
    lName,
    isAbout,
    isPassword,
    isTheme,
    isSecretQ,
    isError,
    fullName,
    partnersName,
    newPassword,
    oldPassword,
    confirmPassword,
    questions,
    isNewError,
    errors,
    formValues,
    resetForm,
    handleClear,
    setIsAbout,
    setSecretQuestion,
    handleAboutClick,
    handlePasswordClick,
    handleThemeClick,
    handleSecretClick,
    handleChange,
    // handleChangePassword,
    handleNewPasswordChange,
    handleOldPasswordChange,
    handleSubmit,
    handleConfirmPasswordChange,
  };
};

export default useProfile;
